import { QueryKey } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

type QueryState<TData, TError> = {
  data: TData | undefined;
  error: TError | null;
  dataUpdatedAt: number;
  errorUpdatedAt: number;
  queryKey: QueryKey;
};

type QueryCallbacks<TData, TError> = {
  onSuccess?: (data: TData) => void;
  onError?: (error: TError) => void;
};

/**
 * The per-query `onSuccess`/`onError` options React Query 5 removed, with
 * their v4 timing: after every successful/failed fetch of the query while
 * mounted (refetches and polling too, even when the data stays equal) - not
 * for data already cached on mount, placeholder data, or when the query key
 * switches to an already cached query.
 *
 * Unlike v4, a manual `setQueryData` also counts as an update.
 */
export const useQueryCallbacks = <TData, TError>(
  query: QueryState<TData, TError>,
  callbacks: QueryCallbacks<TData, TError>,
) => {
  const { data, error, dataUpdatedAt, errorUpdatedAt } = query;
  const key = JSON.stringify(query.queryKey);

  // Callbacks of the latest render, like v4's observer options.
  const latestCallbacks = useRef(callbacks);
  latestCallbacks.current = callbacks;

  const seen = useRef({ key, dataUpdatedAt, errorUpdatedAt });

  useEffect(() => {
    const previous = seen.current;
    seen.current = { key, dataUpdatedAt, errorUpdatedAt };

    // Another query's cached state isn't a fetch of this one.
    if (previous.key !== key) {
      return;
    }

    if (dataUpdatedAt !== previous.dataUpdatedAt && data !== undefined) {
      latestCallbacks.current.onSuccess?.(data);
    }

    if (errorUpdatedAt !== previous.errorUpdatedAt && error) {
      latestCallbacks.current.onError?.(error);
    }
    // `data`/`error` change together with their timestamps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, dataUpdatedAt, errorUpdatedAt]);
};
